import type { OrthoView, Vector3 } from "viewer/constants";
import type { AnnotationTool } from "viewer/model/accessors/tool_accessor";
import type { BorderOpenStatus, Theme, WebknossosState } from "viewer/store";
import type { StartAiJobDrawerState } from "viewer/view/ai_jobs/constants";

type SetDropzoneModalVisibilityAction = ReturnType<typeof setDropzoneModalVisibilityAction>;
type SetVersionRestoreVisibilityAction = ReturnType<typeof setVersionRestoreVisibilityAction>;
type SetIsRestoringVersionAction = ReturnType<typeof setIsRestoringVersionAction>;
type SetStoredLayoutsAction = ReturnType<typeof setStoredLayoutsAction>;
type SetBorderOpenStatusAction = ReturnType<typeof setBorderOpenStatusAction>;
type SetImportingMeshStateAction = ReturnType<typeof setImportingMeshStateAction>;
type SetIsInAnnotationViewAction = ReturnType<typeof setIsInAnnotationViewAction>;
type SetHasOrganizationsAction = ReturnType<typeof setHasOrganizationsAction>;
export type SetToolAction = ReturnType<typeof setToolAction>;
export type CycleToolAction = ReturnType<typeof cycleToolAction>;
type SetThemeAction = ReturnType<typeof setThemeAction>;
type SetDownloadModalVisibilityAction = ReturnType<typeof setDownloadModalVisibilityAction>;
type SetShareModalVisibilityAction = ReturnType<typeof setShareModalVisibilityAction>;
type SetIsWkInitializedAction = ReturnType<typeof setIsWkInitializedAction>;
type SetPythonClientModalVisibilityAction = ReturnType<typeof setPythonClientModalVisibilityAction>;
type SetaIJobDrawerStateAction = ReturnType<typeof setAIJobDrawerStateAction>;
export type EnterAction = ReturnType<typeof enterAction>;
export type EscapeAction = ReturnType<typeof escapeAction>;
export type SetQuickSelectStateAction = ReturnType<typeof setQuickSelectStateAction>;
type ShowQuickSelectSettingsAction = ReturnType<typeof showQuickSelectSettingsAction>;
type HideMeasurementTooltipAction = ReturnType<typeof hideMeasurementTooltipAction>;
type SetLastMeasuredPositionAction = ReturnType<typeof setLastMeasuredPositionAction>;
type SetIsMeasuringAction = ReturnType<typeof setIsMeasuringAction>;
type SetVoxelPipetteTooltipPinnedPositionAction = ReturnType<
  typeof setVoxelPipetteTooltipPinnedPositionAction
>;
type SetNavbarHeightAction = ReturnType<typeof setNavbarHeightAction>;
type ShowContextMenuAction = ReturnType<typeof showContextMenuAction>;
type HideContextMenuAction = ReturnType<typeof hideContextMenuAction>;
type SetActiveUserBoundingBoxId = ReturnType<typeof setActiveUserBoundingBoxId>;
type SetGlobalProgressAction = ReturnType<typeof setGlobalProgressAction>;

type SetRenderAnimationModalVisibilityAction = ReturnType<
  typeof setRenderAnimationModalVisibilityAction
>;
type SetUserScriptsModalVisibilityAction = ReturnType<typeof setUserScriptsModalVisibilityAction>;
type SetZarrLinksModalVisibilityAction = ReturnType<typeof setZarrLinksModalVisibilityAction>;
type SetKeyboardShortcutConfigModalVisibilityAction = ReturnType<
  typeof setKeyboardShortcutConfigModalVisibilityAction
>;
type SetMergeModalVisibilityAction = ReturnType<typeof setMergeModalVisibilityAction>;
type SetDuplicateAnnotationModalVisibilityAction = ReturnType<
  typeof setDuplicateAnnotationModalVisibilityAction
>;

export type UiAction =
  | SetDropzoneModalVisibilityAction
  | SetVersionRestoreVisibilityAction
  | SetIsRestoringVersionAction
  | SetImportingMeshStateAction
  | SetBorderOpenStatusAction
  | SetStoredLayoutsAction
  | SetIsInAnnotationViewAction
  | SetHasOrganizationsAction
  | SetToolAction
  | CycleToolAction
  | SetThemeAction
  | SetDownloadModalVisibilityAction
  | SetPythonClientModalVisibilityAction
  | SetShareModalVisibilityAction
  | SetaIJobDrawerStateAction
  | SetRenderAnimationModalVisibilityAction
  | SetMergeModalVisibilityAction
  | SetUserScriptsModalVisibilityAction
  | SetZarrLinksModalVisibilityAction
  | SetKeyboardShortcutConfigModalVisibilityAction
  | SetDuplicateAnnotationModalVisibilityAction
  | SetIsWkInitializedAction
  | EnterAction
  | EscapeAction
  | SetQuickSelectStateAction
  | ShowQuickSelectSettingsAction
  | HideMeasurementTooltipAction
  | SetLastMeasuredPositionAction
  | SetIsMeasuringAction
  | SetVoxelPipetteTooltipPinnedPositionAction
  | SetNavbarHeightAction
  | ShowContextMenuAction
  | HideContextMenuAction
  | SetActiveUserBoundingBoxId
  | SetGlobalProgressAction;

export const setDropzoneModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_DROPZONE_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setVersionRestoreVisibilityAction = (active: boolean) =>
  ({
    type: "SET_VERSION_RESTORE_VISIBILITY",
    active,
  }) as const;
export const setIsRestoringVersionAction = (isRestoring: boolean) =>
  ({
    type: "SET_IS_RESTORING_VERSION",
    isRestoring,
  }) as const;
export const setStoredLayoutsAction = (storedLayouts: Record<string, any>) =>
  ({
    type: "SET_STORED_LAYOUTS",
    storedLayouts,
  }) as const;
export const setBorderOpenStatusAction = (borderOpenStatus: BorderOpenStatus) =>
  ({
    type: "SET_BORDER_OPEN_STATUS",
    borderOpenStatus,
  }) as const;
const setImportingMeshStateAction = (isImporting: boolean) =>
  ({
    type: "SET_IMPORTING_MESH_STATE",
    isImporting,
  }) as const;
export const setIsInAnnotationViewAction = (value: boolean) =>
  ({
    type: "SET_IS_IN_ANNOTATION_VIEW",
    value,
  }) as const;
export const setHasOrganizationsAction = (value: boolean) =>
  ({
    type: "SET_HAS_ORGANIZATIONS",
    value,
  }) as const;
export const setToolAction = (tool: AnnotationTool) =>
  ({
    type: "SET_TOOL",
    tool,
  }) as const;
export const cycleToolAction = (backwards: boolean = false) =>
  ({
    type: "CYCLE_TOOL",
    backwards,
  }) as const;
export const setThemeAction = (value: Theme) =>
  ({
    type: "SET_THEME",
    value,
  }) as const;
export const setDownloadModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_DOWNLOAD_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setShareModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_SHARE_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setAIJobDrawerStateAction = (state: StartAiJobDrawerState) =>
  ({
    type: "SET_AI_JOB_DRAWER_STATE",
    state,
  }) as const;
export const setRenderAnimationModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_CREATE_ANIMATION_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setMergeModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_MERGE_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setUserScriptsModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_USER_SCRIPTS_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setZarrLinksModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_ZARR_LINKS_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setKeyboardShortcutConfigModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_KEYBOARD_SHORTCUT_CONFIG_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setDuplicateAnnotationModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_DUPLICATE_ANNOTATION_MODAL_VISIBILITY",
    visible,
  }) as const;
export const setIsWkInitializedAction = (isInitialized: boolean) =>
  ({
    type: "SET_IS_WK_INITIALIZED",
    isInitialized,
  }) as const;

export const setPythonClientModalVisibilityAction = (visible: boolean) =>
  ({
    type: "SET_PYTHON_MODAL_VISIBILITY",
    visible,
  }) as const;
export const enterAction = (event: KeyboardEvent) =>
  ({
    type: "ENTER",
    event,
  }) as const;
export const escapeAction = () =>
  ({
    type: "ESCAPE",
  }) as const;
export const setQuickSelectStateAction = (
  state: WebknossosState["uiInformation"]["quickSelectState"],
) =>
  ({
    type: "SET_QUICK_SELECT_STATE",
    state,
  }) as const;
export const showQuickSelectSettingsAction = (isOpen: boolean) =>
  ({
    type: "SET_ARE_QUICK_SELECT_SETTINGS_OPEN",
    isOpen,
  }) as const;
export const hideMeasurementTooltipAction = () =>
  ({
    type: "HIDE_MEASUREMENT_TOOLTIP",
  }) as const;
export const setLastMeasuredPositionAction = (position: Vector3) =>
  ({
    type: "SET_LAST_MEASURED_POSITION",
    position,
  }) as const;
export const setIsMeasuringAction = (isMeasuring: boolean) =>
  ({
    type: "SET_IS_MEASURING",
    isMeasuring,
  }) as const;

export const setVoxelPipetteTooltipPinnedPositionAction = (position: Vector3 | null) =>
  ({
    type: "SET_VOXEL_PIPETTE_TOOLTIP_PINNED_POSITION",
    position,
  }) as const;

export const setNavbarHeightAction = (navbarHeight: number) =>
  ({
    type: "SET_NAVBAR_HEIGHT",
    navbarHeight,
  }) as const;

export const showContextMenuAction = (
  xPos: number,
  yPos: number,
  nodeId: number | null | undefined,
  boundingBoxId: number | null | undefined,
  globalPosition: Vector3 | null | undefined,
  viewport: OrthoView,
  meshId: number | null | undefined,
  meshIntersectionPosition: Vector3 | null | undefined,
  unmappedSegmentId: number | undefined | null,
) =>
  ({
    type: "SHOW_CONTEXT_MENU",
    contextMenuPosition: [xPos, yPos],
    clickedNodeId: nodeId,
    clickedBoundingBoxId: boundingBoxId,
    globalPosition,
    viewport,
    meshId,
    meshIntersectionPosition,
    unmappedSegmentId,
  }) as const;

export const hideContextMenuAction = () =>
  ({
    type: "HIDE_CONTEXT_MENU",
  }) as const;

export const setActiveUserBoundingBoxId = (id: number | null) => {
  return {
    type: "SET_ACTIVE_USER_BOUNDING_BOX_ID",
    id,
  } as const;
};

export const setGlobalProgressAction = (value: number) => {
  return {
    type: "SET_GLOBAL_PROGRESS",
    value,
  } as const;
};
